import React, {useState, useEffect, useRef} from 'react';
import {useForm} from 'react-hook-form';
import {useLocation, useNavigate} from 'react-router-dom';
import {useTitle} from '../../../components/General/Layout';
import LoadingWheel from '../../../components/General/LoadingWheel';
import MaintenanceHeader from '../../../components/Surveys/SurveyMaintenanceHeader';
import ErrorHelper from '../../../service/ErrorHelper';
import {SMaster, Survey} from '../../../service/Urls';
import {get, post} from '../../../service/ApiService';
import SatisfactionSection from '../../../components/Surveys/SatisfactionSection';
import {Box, Flex, Heading, Spacer, Textarea} from '@chakra-ui/react';
import SurveySideBar from '../../../components/Surveys/SurveySideBar';
import FlexCard from '../../../components/General/FlexCard';
import SatisfactionToTen from '../../../components/Surveys/SatisfactionToTen';
import {CustomCheckbox} from '../../../components/General/FormInputs';
import BoxCard from '../../../components/General/BoxCard';
import {formatDate} from '../../../helpers/Functions';

const EventPlanningMaintenance = () => {
  const [data, setData] = useState({});
  const [smasterData, setSmasterData] = useState({});
  const [toast, setToast] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isUpdate, setIsUpdate] = useState(true);
  const [ids, setIds] = useState([]);

  const [title, setTitle] = useTitle('Event Planning Survey Maintenance');

  const isMounted = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();
  const {
    register,
    handleSubmit,
    control,
    reset,
    getValues,
    formState: {errors},
  } = useForm();

  useEffect(() => {
    isMounted.current = true;

    loadData(location?.state?.id);

    return () => {
      isMounted.current = false;
    };
  }, []);

  const loadData = async masterId => {
    const data = await get(Survey.GET + `/` + masterId);
    setToast(data);
    if (!data.error) {
      if (!data.dateAdded) {
        data.dateAdded = localStorage.getItem('prevDateAdded') || new Date().toISOString().split('T')[0];
        setIsUpdate(false);
      } else {
        // For a datepicker to default a value it must be in yyyy-mm-dd.  Modify initial value then call reset on the data to get that defaulted
        data.dateAdded = data.dateAdded.split('T')[0];
      }
      setData(data);
      reset(data);
      const smasterResponse = await get(SMaster.RECORD + '/' + masterId);
      setToast(smasterResponse);
      if (!smasterResponse.error) {
        smasterResponse['contractDate'] = formatDate(smasterResponse.smCondate);
        setSmasterData(smasterResponse);
      }
    }
    setIsLoading(false);
    setTitle('Event Planning Survey Maintenance');
    assignIds();
  };

  // ids for the good/excellent/na questions for the buttons to auto assign
  const assignIds = () => {
    let arr = [];
    const prompts = [prompt3, prompt4, prompt5, prompt6, prompt7, prompt8];
    prompts.forEach(promptArr => {
      arr = arr.concat(promptArr.map(prompt => prompt.id));
    });
    setIds(arr);
  };

  const submitSurvey = async formData => {
    formData.svRecord = data.svRecord ?? smasterData.smRecord;
    formData.svClient = data.svClient ?? smasterData.smClient;
    formData.svLoc = data.svLoc ?? smasterData.smLoc;
    formData.svGroup = data.svGroup ?? smasterData.smGroup;

    formData.clientId = smasterData.nclientpk;
    formData.groupId = smasterData.ngrouppk;
    formData.locationId = smasterData.locationpk;
    formData.sMasterId = smasterData.npk;

    formData.svPos = data.svPos;
    formData.svNeg = data.svNeg;
    formData.svNeedatt = data.svNeedatt;

    let url = Survey.ADD_EVENT_PLANNING;
    let successMessage = 'Successfully added survey for ' + smasterData.clientName;
    if (isUpdate) {
      url = Survey.UPDATE_EVENT_PLANNING + '/' + formData.id;
      successMessage = 'Successfully updated survey for ' + smasterData.clientName;
    }

    setIsSaving(true);

    const response = await post(url, formData);

    if (isMounted.current === false) return;

    setIsSaving(false);

    setToast({
      response: response,
      successMessage: successMessage,
      onSuccess: () => navigate('/surveys/lookup', {state: {serviceType: location?.state?.serviceType}}),
    });
  };

  // The ids must correspond to the data that is sent from api, otherwise defaults and sending data up won't work
  const prompt3 = [
    {prompt: '3a. Cleanliness', id: 'sv3a'},
    {prompt: 'b. Timeliness', id: 'sv3b'},
  ];

  const prompt4 = [
    {prompt: '4a. Knowledgeable', id: 'sv4a'},
    {prompt: 'b. Options', id: 'sv4b'},
    {prompt: 'c. Needs', id: 'sv4c'},
    {prompt: 'd. Changes', id: 'sv4d'},
    {prompt: 'e. Payment', id: 'sv4e'},
    {prompt: 'f. Detail', id: 'sv4f'},
  ];

  const prompt5 = [
    {prompt: '5a. Knowledgeable', id: 'sv5a'},
    {prompt: 'b. Event Set-up', id: 'sv5b'},
    {prompt: 'c. Professional', id: 'sv5c'},
    {prompt: 'd. Service of Staff', id: 'sv5d'},
  ];

  const prompt6 = [{prompt: '6a. Support', id: 'sv6a'}];
  const prompt7 = [{prompt: '7a. Overall Sat', id: 'sv7a'}];
  const prompt8 = [{prompt: '8a. Value', id: 'sv8a'}];

  return (
    <>
      {isLoading ? (
        <>
          <LoadingWheel />
          <ErrorHelper toastResponse={toast} />
        </>
      ) : (
        <form onSubmit={handleSubmit(submitSurvey)}>
          <Box w={{base: '100%', lg: '90%'}}>
            <MaintenanceHeader
              register={register}
              control={control}
              errors={errors}
              masterData={smasterData}
              data={data}
              noDeceased
              setData={setData}
              reset={reset}
              ids={ids}
              getValues={getValues}
              submit={handleSubmit(submitSurvey)}
            />

            <Flex>
              <Box w="40%">
                <BoxCard textAlign="start">
                  <Flex>
                    <Heading size="sm" mr="4">
                      1.
                    </Heading>
                    <Box textAlign="start">
                      <CustomCheckbox name="Attended Event There" id={'sv1a'} control={control} defaultChecked={data.sv1a} />
                      <CustomCheckbox name="Utah Wedding Book" id={'sv1b'} control={control} defaultChecked={data.sv1b} />
                      <CustomCheckbox name="Recommendation" id={'sv1e'} control={control} defaultChecked={data.sv1e} />
                    </Box>
                    <Spacer />
                    <Box textAlign="start">
                      <CustomCheckbox name="Bridal Show" id={'sv1c'} control={control} defaultChecked={data.sv1c} />
                      <CustomCheckbox name="Website" id={'sv1d'} control={control} defaultChecked={data.sv1d} />
                    </Box>
                  </Flex>
                  <Textarea rows={1} id="sv1ed" name="Recommendation" {...register('sv1ed')} errors={errors} />
                  <CustomCheckbox name="Other Advertising" id={'sv1f'} control={control} defaultChecked={data.sv1f} ml="7" />
                  <Textarea rows={1} id="svAdvdesc" name="Other Advertising" {...register('svAdvdesc')} errors={errors} />
                </BoxCard>
                <SatisfactionSection control={control} prompts={prompt3} data={data} />
                <SatisfactionSection control={control} prompts={prompt4} data={data} />
              </Box>
              <Box w="40%">
                <SatisfactionSection control={control} prompts={prompt5} data={data} />
                <SatisfactionSection control={control} prompts={prompt6} data={data} />
                <SatisfactionSection control={control} prompts={prompt7} data={data} />
                <SatisfactionSection control={control} prompts={prompt8} data={data} />
                <SatisfactionToTen control={control} question={{prompt: '9a. Recommendation', id: 'sv9a'}} data={data} />
              </Box>

              <Box w="20%">
                <SurveySideBar
                  control={control}
                  includes={{chinese: false, surveyFormat: false, recommendations: true}}
                  data={data}
                  register={register}
                  errors={errors}
                  svcType={location?.state?.serviceType}
                  isSaving={isSaving}
                />
              </Box>
            </Flex>
            <FlexCard>
              <Textarea rows={3} id="svComment" name="svComment" {...register('svComment')} errors={errors} />
            </FlexCard>
          </Box>
          <ErrorHelper toastResponse={toast} />
        </form>
      )}
    </>
  );
};

export default EventPlanningMaintenance;
